/*
 * The small repeated pieces both TM screens draw.
 *
 * The approved mockup built these by concatenating HTML strings, which is why
 * it carried its own `esc()`. As components they take values, so React escapes
 * for us and the escaping helper is gone rather than ported — a
 * dangerouslySetInnerHTML here would re-open an XSS hole the framework already
 * closes, on data that arrives from a vendor feed.
 */
import { FC, ReactNode } from 'react';

import { feeState, feeTitle, money, shortRule, statusLabel, verdictOf } from './data/format';
import { IRuleRun, ITransaction, ITxnStatus, THRESHOLDS, severityOf } from './data/types';

/* The score, its neon bar, and the two threshold ticks. */
export const ScoreCell: FC<{ score: number; withSeverity?: boolean }> = ({
	score,
	withSeverity,
}) => {
	const sev = severityOf(score);
	const cls = sev.toLowerCase();
	return (
		<>
			<span
				className={`score ${cls}`}
				title={`score ${score} · ${sev} · review ≥${THRESHOLDS.review}, decline ≥${THRESHOLDS.decline}`}
			>
				<span className="sn">{score}</span>
				<span className="bar">
					<i style={{ width: `${Math.min(100, score)}%` }} />
					<b style={{ left: `${THRESHOLDS.review}%` }} />
					<b style={{ left: `${THRESHOLDS.decline}%` }} />
				</span>
			</span>
			{withSeverity && (
				<div className="sevline">
					<span className={`sev ${cls}`}>{sev}</span>
				</div>
			)}
		</>
	);
};

export const StatusChip: FC<{ status: ITxnStatus }> = ({ status }) => (
	<span className={`vch ${verdictOf(status)}`}>{statusLabel(status)}</span>
);

export const TagChips: FC<{ tags: ITransaction['tags'] }> = ({ tags }) => (
	<>
		{(tags ?? []).map(t => (
			<span key={t.name} className={`tag ${t.color}`}>
				{t.name}
			</span>
		))}
	</>
);

/* A rule that sets status, or adds 30+, reads as a hard failure; a TEST rule
   scores nothing and is drawn dashed; anything else merely adds score. */
const ruleTone = (r: IRuleRun) =>
	r.mode === 'TEST'
		? 'test'
		: r.scoreDelta >= 30 || r.actionSummary.includes('change_status')
			? 'fail'
			: 'review';

export const RuleChips: FC<{ runs: IRuleRun[]; max?: number }> = ({ runs, max }) => {
	const matched = (runs ?? []).filter(r => r.matched);
	if (!matched.length) return <span className="nd pass">no rule matched</span>;
	const shown = max ? matched.slice(0, max) : matched;
	return (
		<>
			{shown.map((r, i) => (
				<span key={r.ruleUuid}>
					{i > 0 && <span className="arr">→</span>}
					<span
						className={`nd ${ruleTone(r)}`}
						title={`${r.bundle} · ${r.mode === 'TEST' ? 'test mode, no score' : `+${r.scoreDelta}`}`}
					>
						{shortRule(r.ruleName)}
						{r.mode === 'TEST' ? '' : ` +${r.scoreDelta}`}
					</span>
				</span>
			))}
			{matched.length > shown.length && (
				<span className="bch"> +{matched.length - shown.length} more</span>
			)}
		</>
	);
};

/**
 * A transaction's value in ONE dense table cell.
 *
 * Shared by the investigate deck's evidence table and the historical page's
 * findings table rather than written twice — two copies of a rule about how a
 * trade reads is exactly how the two surfaces end up disagreeing about the same
 * transaction.
 *
 * A payment is just its amount. A TRADE cannot show both legs at this width — the
 * evidence table is eight columns — so it shows the shape (`T · BTC/USD`) and
 * puts the full breakdown in the title: both legs, and the reportable figure that
 * actually fed the scoring. The ticker pair is the part an analyst scans for; the
 * numbers are the part they hover for.
 */
export const TradeAmountCell: FC<{ txn: ITransaction }> = ({ txn }) => {
	const s = txn.legs?.sent;
	const r = txn.legs?.received;
	/* The ƒ marker appears for BOTH 'present' and 'zero' — an explicitly reported
	   zero is a fact worth hovering. Only 'unsupplied' gets nothing, which is
	   what this cell already looked like, so a row with no fee data renders
	   exactly as it did before this change. */
	const flag =
		feeState(txn) !== 'unsupplied' ? (
			<sup className="feeflag" aria-label="fee information">
				ƒ
			</sup>
		) : null;

	if (txn.transactionKind !== 'trade' || !s || !r) {
		return (
			<span title={feeTitle(txn)}>
				{money(txn.amount, txn.currency, txn.currencyKind)}
				{flag}
			</span>
		);
	}
	return (
		<span className="tradecell" title={feeTitle(txn)}>
			<span className="nd review">T</span> {s.currency}/{r.currency}
			{flag}
		</span>
	);
};

/* A key/value row, dropped entirely when there is nothing to show — the rule the
   session drawer already follows rather than printing an empty cell. */
export const KVRows: FC<{ rows: [string, ReactNode, string?][] }> = ({ rows }) => (
	<>
		{rows
			.filter(r => r[1] !== null && r[1] !== undefined && r[1] !== '')
			.map(([k, v, cls]) => (
				<tr key={k}>
					<td>{k}</td>
					<td className={cls}>{v}</td>
				</tr>
			))}
	</>
);

/* Every empty state on these screens says which query came back empty, never a
   bare "No data" — an empty queue and a failed request look identical otherwise. */
export const Empty: FC<{ children: ReactNode }> = ({ children }) => (
	<div className="sv2-empty">{children}</div>
);
